import cx from 'classnames';
import { FC, Fragment, isValidElement, MouseEvent, ReactNode, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { P, Span } from '../../../typography';
import { AuthConsumer, User } from '../../../containers/shared/auth/AuthContext';
import Can from '../../../containers/shared/auth/Can';
import { getArrayFromObject } from '../../../utils';
import UpgradeDiamond from '../UpgradeDiamond';
import {
  LetterAvatar,
  NavItemWrapper,
  StyledCollapse,
  StyledExpand,
  StyledLink,
  StyledSubMenuLink,
  Tooltip,
} from './SideBarLinkElements';
import { SideBarLinkProps } from './SideBarLink';
import { isPathUnderPrefix } from './activePath';
import { theme } from '../../../theme';

const MobileSideBarLink: FC<SideBarLinkProps> = props => {
  const [expanded, setExpanded] = useState(false);
  const { icon, to, isWeb, title, open, color, bg, subMenu, activePathPrefix, location, onClick } = props;
  const hasSubMenu = !!subMenu;

  useEffect(() => {
    if (location) {
      if (getArrayFromObject(subMenu).some(path => path.to === location.pathname)) {
        // we are in the this route so we open submenu.
        setExpanded(true);
      }
    }
  }, [location]);

  const _isChildActive = () => {
    if (location) {
      return getArrayFromObject(subMenu).some(path => path.to === location.pathname);
    }
    return undefined;
  };

  const _isActive = () => _isChildActive() || isPathUnderPrefix(location && location.pathname, activePathPrefix);

  const toggleExpanded = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    if (!!subMenu) {
      setExpanded(!expanded);
    }
  };

  const _renderIcon = (icon: ReactNode) => {
    if (isValidElement(icon)) {
      return icon;
    }
    return null;
  };

  const _getLetters = (title: string) => {
    let matches = title.match(/\b(\w)/g);
    if (matches) {
      if (matches.length < 3) {
        return matches.join('');
      } else {
        const sliced = matches.slice(1, 3);
        return sliced.join('');
      }
    }
    return '';
  };

  const _renderSubMenu = (
    subMenu: Array<{
      to: string;
      title: string;
      check?: (user: User, hasCaseManagement: boolean) => boolean;
      locked?: (user: User) => boolean;
    }>,
    { ...props }
  ) => {
    return subMenu.map(
      (item: {
        to: string;
        title: string;
        check?: (user: User, hasCaseManagement: boolean) => boolean;
        locked?: (user: User) => boolean;
      }) => (
        <Can
          logout={props.logout}
          landingPage={false}
          key={item.title}
          roles={props.user.roles}
          plan={props.user.plan}
          userAddOns={props.user.addOns}
          action={`${item.to}:visit`}
          navItemCheck={item.check ? item.check(props.user, props.hasCaseManagement) : undefined}
          yes={() => (
            <StyledSubMenuLink
              to={item.to}
              key={item.title}
              activeClassName="active-submenu-link"
              className={props.open ? 'nav-link' : 'nav-link-text-hidden'}
            >
              <NavItemWrapper open={props.open} isWeb={isWeb}>
                {props.open ? (
                  <Span
                    pointer={true}
                    ml="60px"
                    color={theme.colors.primary}
                    onClick={onClick}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {item.title}
                    {item.locked && item.locked(props.user) ? <UpgradeDiamond size={12} /> : null}
                  </Span>
                ) : (
                  <LetterAvatar open={props.open}>
                    <P className="abbreviated-letters">{_getLetters(item.title)}</P>
                    <Tooltip>
                      <P p={0} m={0}>
                        {item.title}
                      </P>
                    </Tooltip>
                  </LetterAvatar>
                )}
              </NavItemWrapper>
            </StyledSubMenuLink>
          )}
          no={() => null}
        />
      )
    );
  };

  return (
    <Fragment>
      <StyledLink
        to={to}
        open={expanded}
        onClick={hasSubMenu ? toggleExpanded : () => null}
        className={cx('nav-link', { 'nav-link-text-hidden': !open }, { 'child-link-active': _isActive() })}
      >
        <NavItemWrapper open={open} bg={bg} isWeb={isWeb}>
          {_renderIcon(icon)}
          <Span pointer={true} ml="20px" color={color} fontSize="0.7em" onClick={onClick}>
            {title}
          </Span>
          {subMenu && <StyledExpand size={20} open={open} expanded={expanded.toString()} />}
          {!open && (
            <Tooltip>
              <P>{title}</P>
            </Tooltip>
          )}
        </NavItemWrapper>
      </StyledLink>

      {/* Submenus */}
      <AuthConsumer>
        {({ user, logout, hasCaseManagement }) => {
          return (
            <StyledCollapse active={_isChildActive()} open={expanded} sidebarOpen={open} allowOverflow={expanded}>
              {subMenu &&
                typeof subMenu !== undefined &&
                _renderSubMenu(subMenu, { open, color, user, logout, hasCaseManagement })}
            </StyledCollapse>
          );
        }}
      </AuthConsumer>
    </Fragment>
  );
};

MobileSideBarLink.defaultProps = {
  color: '#FFF',
};

export default withRouter(MobileSideBarLink);
